import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, Users, UserCog } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export function PrincipalUserManagement() {
  const { user } = useAuth();
  const [principals, setPrincipals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingPrincipal, setEditingPrincipal] = useState(null);
  const [formData, setFormData] = useState({
    email: '',
    name: '',
    password: ''
  });
  
  // System Settings
  const [systemSettings, setSystemSettings] = useState(null);
  const [customDomain, setCustomDomain] = useState('');
  const [savingDomain, setSavingDomain] = useState(false);

  // Verificar que el usuario sea super_admin
  if (user?.role !== 'super_admin') {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Card className="p-6">
          <p className="text-slate-600">Solo los Super Administradores pueden acceder a esta página.</p>
        </Card>
      </div>
    );
  }

  useEffect(() => {
    fetchPrincipals();
    fetchSystemSettings();
  }, []);

  const fetchPrincipals = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/users/admins`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPrincipals(response.data);
    } catch (error) {
      console.error('Error fetching principals:', error);
      toast.error('Error al cargar los usuarios principales');
    } finally {
      setLoading(false);
    }
  };

  const fetchSystemSettings = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/system-settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSystemSettings(response.data);
      setCustomDomain(response.data.custom_domain || '');
    } catch (error) {
      console.error('Error fetching system settings:', error);
    }
  };

  const handleSaveDomain = async () => {
    try {
      setSavingDomain(true);
      const token = localStorage.getItem('token');
      await axios.put(
        `${API_URL}/api/system-settings`,
        { custom_domain: customDomain },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Dominio personalizado guardado correctamente');
      fetchSystemSettings();
    } catch (error) {
      console.error('Error saving custom domain:', error);
      toast.error('Error al guardar el dominio personalizado');
    } finally {
      setSavingDomain(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validar campos obligatorios
    if (!formData.email || !formData.name) {
      toast.error('Email y nombre son obligatorios');
      return;
    }

    if (!editingPrincipal && !formData.password) {
      toast.error('La contraseña es obligatoria para nuevos usuarios');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      
      if (editingPrincipal) {
        // Actualizar usuario principal
        const updateData = {
          name: formData.name,
          ...(formData.password && { password: formData.password })
        };
        
        await axios.put(
          `${API_URL}/api/users/admins/${editingPrincipal.id}`,
          updateData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Usuario Principal actualizado correctamente');
      } else {
        // Crear nuevo usuario principal
        await axios.post(
          `${API_URL}/api/users/admins`,
          formData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Usuario Principal creado correctamente');
      }

      setDialogOpen(false);
      setFormData({ email: '', name: '', password: '' });
      setEditingPrincipal(null);
      fetchPrincipals();
    } catch (error) {
      console.error('Error saving principal:', error);
      const message = error.response?.data?.detail || 'Error al guardar el usuario principal';
      toast.error(message);
    }
  };

  const handleDelete = async (principalId) => {
    if (!window.confirm('⚠️ ¿Estás seguro de que deseas eliminar este Usuario Principal?\n\nSe eliminarán permanentemente:\n- El usuario\n- Todos sus asistentes\n- Todas sus pólizas\n- Todos sus datos')) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_URL}/api/users/admins/${principalId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Usuario Principal eliminado correctamente');
      fetchPrincipals();
    } catch (error) {
      console.error('Error deleting principal:', error);
      toast.error('Error al eliminar el usuario principal');
    }
  };

  const openEditDialog = (principal) => {
    setEditingPrincipal(principal);
    setFormData({
      email: principal.email,
      name: principal.name,
      password: '' // No mostrar contraseña existente
    });
    setDialogOpen(true);
  };

  const openCreateDialog = () => {
    setEditingPrincipal(null);
    setFormData({ email: '', name: '', password: '' });
    setDialogOpen(true);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-slate-500">Cargando...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 p-6">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-slate-900 flex items-center gap-2">
            <UserCog className="w-8 h-8 text-[#2537b0]" />
            Panel de Super Administrador
          </h1>
          <p className="text-slate-600 mt-1">Configuración global del sistema</p>
        </div>

        {/* System Settings - Custom Domain */}
        <Card className="p-6 mb-6 bg-gradient-to-br from-blue-50 to-indigo-50 border-blue-200">
          <div className="mb-4">
            <h2 className="text-xl font-semibold text-slate-900 flex items-center gap-2">
              🌐 Dominio Personalizado del Portal
            </h2>
            <p className="text-sm text-slate-600 mt-1">
              Configura el dominio personalizado para los links del portal de clientes
            </p>
          </div>
          
          <div className="bg-white rounded-lg p-4 mb-4">
            <Label htmlFor="customDomain" className="text-sm font-medium text-slate-700">
              Dominio Personalizado
            </Label>
            <div className="flex gap-2 mt-2">
              <Input
                id="customDomain"
                type="text"
                placeholder="ejemplo: asistelo.com"
                value={customDomain}
                onChange={(e) => setCustomDomain(e.target.value)}
                className="flex-1"
              />
              <Button
                onClick={handleSaveDomain}
                disabled={savingDomain}
                className="bg-[#2537b0] hover:bg-[#1c2a8a]"
              >
                {savingDomain ? 'Guardando...' : 'Guardar'}
              </Button>
            </div>
            <p className="text-xs text-slate-500 mt-2">
              Los links del portal se mostrarán como: <code className="bg-slate-100 px-1 py-0.5 rounded">https://{customDomain || 'tudominio.com'}/portal/...</code>
            </p>
          </div>

          {customDomain && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-3">
              <p className="text-sm text-green-800">
                ✅ <strong>Dominio configurado:</strong> https://{customDomain}
              </p>
              <p className="text-xs text-green-700 mt-1">
                Asegúrate de que tu DNS apunte correctamente a la aplicación.
              </p>
            </div>
          )}
        </Card>

        {/* Usuarios Principales Section */}
        <div className="mb-4 flex justify-between items-center">
          <div>
            <h2 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
              <Users className="w-6 h-6 text-[#2537b0]" />
              Usuarios Principales
            </h2>
            <p className="text-slate-600 mt-1">Gestiona los usuarios principales del sistema</p>
          </div>
          
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button onClick={openCreateDialog} className="flex items-center gap-2 bg-[#2537b0] hover:bg-[#1c2a8a]">
                <Plus className="w-4 h-4" />
                Nuevo Usuario Principal
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>
                  {editingPrincipal ? 'Editar Usuario Principal' : 'Nuevo Usuario Principal'}
                </DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label htmlFor="email">Email *</Label>
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    disabled={!!editingPrincipal}
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="name">Nombre *</Label>
                  <Input
                    id="name"
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="password">
                    Contraseña {!editingPrincipal && '*'}
                    {editingPrincipal && ' (dejar en blanco para mantener la actual)'}
                  </Label>
                  <Input
                    id="password"
                    type="password"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    required={!editingPrincipal}
                  />
                </div>
                <div className="flex justify-end gap-2 pt-4">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      setDialogOpen(false);
                      setFormData({ email: '', name: '', password: '' });
                      setEditingPrincipal(null);
                    }}
                  >
                    Cancelar
                  </Button>
                  <Button type="submit" className="bg-[#2537b0] hover:bg-[#1c2a8a]">
                    {editingPrincipal ? 'Actualizar' : 'Crear'}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Lista de usuarios principales */}
        {principals.length === 0 ? (
          <Card className="p-8 text-center">
            <UserCog className="w-16 h-16 mx-auto text-slate-300 mb-4" />
            <p className="text-slate-600 mb-4">No hay usuarios principales registrados</p>
            <Button onClick={openCreateDialog} className="bg-[#2537b0] hover:bg-[#1c2a8a]">
              Crear primer usuario principal
            </Button>
          </Card>
        ) : (
          <div className="space-y-3">
            {principals.map((principal) => (
              <Card key={principal.id} className="p-4 hover:shadow-md transition-shadow">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <h3 className="font-semibold text-slate-900">{principal.name}</h3>
                      <span className="px-2 py-0.5 text-xs font-medium bg-blue-100 text-blue-800 rounded">
                        Usuario Principal
                      </span>
                    </div>
                    <p className="text-sm text-slate-600">{principal.email}</p>
                    <p className="text-xs text-slate-400 mt-1">
                      Creado: {new Date(principal.created_at).toLocaleDateString('es-ES', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric'
                      })}
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => openEditDialog(principal)}
                      className="hover:bg-blue-50"
                    >
                      <Pencil className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="destructive"
                      size="sm"
                      onClick={() => handleDelete(principal.id)}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
